import * as yup from 'yup';

/**
 * Centralized env validation. Throws at module load if required vars are missing
 * or malformed so misconfiguration fails fast instead of producing runtime nulls.
 *
 * Webpack inlines `process.env.NEXT_PUBLIC_*` literals at build time, so the raw
 * lookups below MUST stay literal — do not refactor into a dynamic getter.
 */

// yup 1.x .url() is strict (rejects http://localhost:...) — use native URL constructor instead
const urlString = () =>
  yup
    .string()
    .required()
    .test('is-url', '${path} must be a valid URL', (value) => {
      if (!value) return false;
      try {
        new URL(value);
        return true;
      } catch {
        return false;
      }
    });

const clientSchema = yup.object({
  NEXT_PUBLIC_API: urlString(),
  NEXT_PUBLIC_BUCKET_URL: urlString(),
  NEXT_PUBLIC_APP_VERSION: yup.string().required(),
  NEXT_PUBLIC_GOOGLE_MAPS_KEY: yup.string().required(),
  NEXT_PUBLIC_ENVIRONMENT: yup.string().oneOf(['development', 'staging', 'production']).required()
});

const serverSchema = yup.object({
  NEXTAUTH_URL: urlString(),
  NEXTAUTH_SECRET_KEY: yup.string().required(),
  JWT_TIMEOUT: yup.number().integer().positive().required(),
  JWT_SECRET: yup.string().required()
});

type ClientEnv = yup.InferType<typeof clientSchema>;
type ServerEnv = yup.InferType<typeof serverSchema>;

function formatError(scope: 'client' | 'server', err: yup.ValidationError): string {
  return `[env] Invalid ${scope} environment variables:\n${err.errors.map((e) => `  - ${e}`).join('\n')}`;
}

const clientRaw = {
  NEXT_PUBLIC_API: process.env.NEXT_PUBLIC_API,
  NEXT_PUBLIC_BUCKET_URL: process.env.NEXT_PUBLIC_BUCKET_URL,
  NEXT_PUBLIC_APP_VERSION: process.env.NEXT_PUBLIC_APP_VERSION,
  NEXT_PUBLIC_GOOGLE_MAPS_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY,
  NEXT_PUBLIC_ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT
};

let clientEnv: ClientEnv;
try {
  clientEnv = clientSchema.validateSync(clientRaw, { abortEarly: false });
} catch (err) {
  if (err instanceof yup.ValidationError) throw new Error(formatError('client', err));
  throw err;
}

let serverEnv: ServerEnv | null = null;
if (typeof window === 'undefined') {
  const serverRaw = {
    NEXTAUTH_URL: process.env.NEXTAUTH_URL,
    NEXTAUTH_SECRET_KEY: process.env.NEXTAUTH_SECRET_KEY,
    JWT_TIMEOUT: process.env.JWT_TIMEOUT,
    JWT_SECRET: process.env.JWT_SECRET
  };
  try {
    serverEnv = serverSchema.validateSync(serverRaw, { abortEarly: false });
  } catch (err) {
    if (err instanceof yup.ValidationError) throw new Error(formatError('server', err));
    throw err;
  }
}

export const env = {
  ...clientEnv,
  ...(serverEnv ?? ({} as ServerEnv))
};

export type Env = typeof env;
